import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BarChart3, Filter, Download, Play, Table2, BarChart2
} from 'lucide-react';
import { HorizontalBarChart, DonutChart } from '../../utils/chartComponents';
import toast from 'react-hot-toast';
import statisticsService from '../../services/statisticsService';
import { users, statuses } from '../../services/apiService';
import { getTalentTypeOptions, getTalentTypeLabel } from '../../utils/talentTypeUtils';
import { Loader } from '../UI/LoadingSpinner';
import DateRangePicker from '../UI/DateRangePicker';
import { Pagination, Select, Autocomplete } from '@anyemp/ui-kit';

// Local per-page options for custom report — backend caps at 500, so we offer
// larger sizes than the shared PAGINATION_OPTIONS [10, 20, 50].
const CUSTOM_REPORT_PAGINATION_OPTIONS = [
  { value: 20, label: '20' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
  { value: 200, label: '200' }
];
import Avatar from '../UI/Avatar';
import { exportCustomReportCSV } from '../../utils/statisticsExportUtils';

const DIMENSION_OPTIONS = [
  { value: 'talent_type', label: 'Talent Type' },
  { value: 'status', label: 'Status' },
  { value: 'manager_id', label: 'Manager' },
  { value: 'is_active', label: 'Active / Inactive' },
  { value: 'profession', label: 'Profession' },
  { value: 'department', label: 'Department' },
  { value: 'country_id', label: 'Country' },
  { value: 'city_id', label: 'City' },
  { value: 'created_month', label: 'Created Month' },
  { value: 'created_year', label: 'Created Year' }
];

import { CHART_COLORS } from '../../utils/chartTheme';
import StatisticsTable from './StatisticsTable';

const CustomReportsStats = () => {
  const [dimensions, setDimensions] = useState(['talent_type']);
  const [filters, setFilters] = useState({
    talent_types: [],
    status_ids: [],
    manager_ids: [],
    is_active: '',
    created_from: '',
    created_to: ''
  });
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_items: 0, items_per_page: 100 });
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);

  // Справочные данные для фильтров
  const [managers, setManagers] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  const talentTypeOptions = getTalentTypeOptions();

  const runReport = useCallback(async () => {
    if (!dimensions.length) {
      toast.error('Select at least one dimension');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const queryFilters = { ...filters };
      if (queryFilters.is_active === '') delete queryFilters.is_active;
      else queryFilters.is_active = queryFilters.is_active === 'true';

      // Удаляем пустые массивы
      Object.keys(queryFilters).forEach(key => {
        if (Array.isArray(queryFilters[key]) && queryFilters[key].length === 0) {
          delete queryFilters[key];
        }
        if (queryFilters[key] === '' || queryFilters[key] === undefined) {
          delete queryFilters[key];
        }
      });

      const response = await statisticsService.getCustomReport({
        dimensions,
        metrics: ['count'],
        filters: queryFilters,
        limit,
        offset
      });

      // response содержит { rows, totals, dimensions_meta, pagination }
      if (response && response.rows) {
        setResults(response);

        if (response.pagination) {
          setPagination({
            current_page: Math.floor(offset / limit) + 1,
            total_pages: Math.ceil(response.pagination.total / limit),
            total_items: response.pagination.total,
            items_per_page: limit
          });
        }
      }
    } catch (err) {
      console.error('Error running custom report:', err);
      setError(err.response?.data?.message || err.message || 'Failed to run report');
      toast.error('Failed to run report');
    } finally {
      setLoading(false);
    }
  }, [dimensions, filters, limit, offset]);

  const handlePageChange = (newPage) => {
    const newOffset = (newPage - 1) * limit;
    setOffset(newOffset);
  };

  // Загружаем справочные данные для фильтров
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        // Загружаем всех пользователей
        const usersResponse = await users.getAllUsers();
        // Структура: { data: { users: [...] } }
        const usersArray = usersResponse.data?.users || usersResponse.users || usersResponse.data || usersResponse;
        const managersList = (Array.isArray(usersArray) ? usersArray : []).map(u => ({
          value: String(u.id),
          label: u.fullName || u.name || u.email
        }));
        setManagers(managersList);

        // Загружаем все статусы
        const statusesResponse = await statuses.getAllStatuses({ all: true });
        const statusesData = statusesResponse.data || statusesResponse;
        const statusesList = (Array.isArray(statusesData) ? statusesData : []).map(s => ({
          value: String(s.id),
          label: s.name
        }));
        setStatusOptions(statusesList);
      } catch (error) {
        console.error('Error loading reference data:', error);
      }
    };

    loadReferenceData();
  }, []);

  // Перезапускаем при изменении пагинации
  useEffect(() => {
    if (results) {
      runReport();
    }
  }, [offset, limit]);

  const formatDimensionValue = useCallback((dimKey, value, row) => {
    switch (dimKey) {
      case 'status':
        return (
          <span className="flex flex-wrap items-center gap-2">
            <span
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: row?.status_data?.color || row?.status_color || '#6B7280' }}
            />
            {row?.status_data?.name || value || 'No Status'}
          </span>
        );
      case 'talent_type':
        return getTalentTypeLabel(value) || value || 'N/A';
      case 'is_active':
        return value === true || value === 'true' ? 'Active' : 'Inactive';
      case 'manager_id':
        if (!row?.manager) return value || 'Unassigned';
        return (
          <div className="flex flex-wrap items-center gap-2">
            <Avatar
              avatar={row.manager.avatar}
              name={row.manager.fullName || row.manager.name || row.manager.email}
              size="sm"
            />
            <span>{row.manager.fullName || row.manager.name || row.manager.email}</span>
          </div>
        );
      case 'country_id':
        return row?.country?.name || value || 'N/A';
      case 'city_id':
        return row?.city?.name || value || 'N/A';
      case 'profession':
        return row?.profession_data?.name || value || 'N/A';
      case 'department':
        return row?.department_data?.name || value || 'N/A';
      case 'created_month':
        if (!value) return 'N/A';
        try {
          return new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
        } catch { return value; }
      case 'created_year':
        if (!value) return 'N/A';
        try {
          return new Date(value).toLocaleDateString('en-US', { year: 'numeric' });
        } catch { return value; }
      default:
        return value ?? 'N/A';
    }
  }, []);

  const formatDimensionValueText = useCallback((dimKey, value, row) => {
    switch (dimKey) {
      case 'status': return row?.status_data?.name || value || 'No Status';
      case 'talent_type': return getTalentTypeLabel(value) || value || 'N/A';
      case 'is_active': return value === true || value === 'true' ? 'Active' : 'Inactive';
      case 'manager_id': return row?.manager?.fullName || row?.manager?.name || row?.manager?.email || value || 'Unassigned';
      case 'country_id': return row?.country?.name || value || 'N/A';
      case 'city_id': return row?.city?.name || value || 'N/A';
      case 'profession': return row?.profession_data?.name || value || 'N/A';
      case 'department': return row?.department_data?.name || value || 'N/A';
      case 'created_month':
        if (!value) return 'N/A';
        try { return new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short' }); }
        catch { return value; }
      case 'created_year':
        if (!value) return 'N/A';
        try { return new Date(value).toLocaleDateString('en-US', { year: 'numeric' }); }
        catch { return value; }
      default: return value ?? 'N/A';
    }
  }, []);

  const tableColumns = useMemo(() => {
    if (!results?.rows?.length) return [];

    const cols = dimensions.map(dimKey => {
      const dimOption = DIMENSION_OPTIONS.find(d => d.value === dimKey);
      return {
        label: dimOption?.label || dimKey,
        key: dimKey,
        sortable: true,
        render: (item) => (
          <span className="text-gray-900 dark:text-gray-100">
            {formatDimensionValue(dimKey, item[dimKey], item)}
          </span>
        )
      };
    });

    cols.push({
      label: 'Count',
      key: 'count',
      sortable: true,
      render: (item) => (
        <span className="font-semibold text-gray-900 dark:text-gray-100">
          {item.count}
        </span>
      )
    });

    return cols;
  }, [results, dimensions, formatDimensionValue]);

  const rowKey = useCallback((row, index) => row.id ?? index, []);

  const visibleColumns = useMemo(() => {
    const cols = {};
    dimensions.forEach(d => { cols[d] = true; });
    cols.count = true;
    return cols;
  }, [dimensions]);

  // Chart data
  const chartData = useMemo(() => {
    if (!results?.rows?.length) return [];
    return results.rows.slice(0, 20).map((row, i) => {
      const label = formatDimensionValueText(dimensions[0], row[dimensions[0]], row);
      return {
        name: typeof label === 'string' ? label : String(label),
        count: parseInt(row.count) || 0,
        fill: CHART_COLORS[i % CHART_COLORS.length]
      };
    });
  }, [results, dimensions, formatDimensionValueText]);

  const handleExport = () => {
    if (!results?.rows?.length) return;
    exportCustomReportCSV(results.rows, dimensions, DIMENSION_OPTIONS, formatDimensionValueText);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <BarChart3 className="h-8 w-8 text-primary-600" />
          <div>
            <h1 className="break-words text-2xl font-bold text-gray-900 dark:text-gray-100">
              Custom Reports
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Build custom reports by selecting dimensions and filters
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* View mode toggle */}
          {results?.rows?.length > 0 && (
            <div className="flex max-w-full overflow-x-auto rounded-lg border border-gray-300 dark:border-gray-600">
              <button
                onClick={() => setViewMode('table')}
                title="Table view"
                className={`flex shrink-0 items-center gap-1.5 px-3 py-2 text-sm transition-colors ${
                  viewMode === 'table'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <Table2 className="h-4 w-4" />
                <span>Table</span>
              </button>
              <button
                onClick={() => setViewMode('chart')}
                title="Chart view"
                className={`flex shrink-0 items-center gap-1.5 border-l px-3 py-2 text-sm transition-colors border-gray-300 dark:border-gray-600 ${
                  viewMode === 'chart'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <BarChart2 className="h-4 w-4" />
                <span>Chart</span>
              </button>
            </div>
          )}

          {/* Export */}
          {results?.rows?.length > 0 && (
            <button
              onClick={handleExport}
              title="Export to CSV"
              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>CSV</span>
            </button>
          )}

          {/* Filters toggle */}
          <button
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors text-sm ${
              filtersExpanded
                ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-300 dark:border-primary-600 text-primary-700 dark:text-primary-300'
                : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
          </button>
        </div>
      </div>

      {/* Query Builder */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
        {/* Dimensions */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Group By (dimensions)
          </label>
          <Autocomplete
            multiple
            clearable
            fullWidth
            options={DIMENSION_OPTIONS}
            value={dimensions}
            onChange={(val) => setDimensions((val || []).slice(0, 3))}
            placeholder="Select dimensions (max 3)..."
          />
          {dimensions.length >= 3 && (
            <p className="text-xs text-amber-500 mt-1">Maximum 3 dimensions</p>
          )}
        </div>

        {/* Filters panel */}
        {filtersExpanded && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Date range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Created Period
                </label>
                <DateRangePicker
                  startDate={filters.created_from}
                  endDate={filters.created_to}
                  onChange={(range) => setFilters(prev => ({
                    ...prev,
                    created_from: range.startDate || '',
                    created_to: range.endDate || ''
                  }))}
                />
              </div>

              {/* Talent types */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Talent Types
                </label>
                <Autocomplete
                  multiple
                  clearable
                  fullWidth
                  options={talentTypeOptions}
                  value={filters.talent_types}
                  onChange={(val) => setFilters(prev => ({ ...prev, talent_types: val || [] }))}
                  placeholder="All types"
                />
              </div>

              {/* Statuses */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Statuses
                </label>
                <Autocomplete
                  multiple
                  clearable
                  fullWidth
                  options={statusOptions}
                  value={filters.status_ids}
                  onChange={(val) => setFilters(prev => ({ ...prev, status_ids: val || [] }))}
                  placeholder="All statuses"
                />
              </div>

              {/* Managers */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Managers
                </label>
                <Autocomplete
                  multiple
                  clearable
                  fullWidth
                  options={managers}
                  value={filters.manager_ids}
                  onChange={(val) => setFilters(prev => ({ ...prev, manager_ids: val || [] }))}
                  placeholder="All managers"
                />
              </div>

              {/* Active/Inactive */}
              <Select
                label="Activity"
                fullWidth
                value={filters.is_active || ''}
                onChange={(v) => setFilters(prev => ({ ...prev, is_active: v || '' }))}
                options={[
                  { value: '', label: 'All' },
                  { value: 'true', label: 'Active' },
                  { value: 'false', label: 'Inactive' },
                ]}
              />
            </div>
          </div>
        )}

        {/* Run button */}
        <div className="flex justify-end pt-2">
          <button
            onClick={() => { setOffset(0); runReport(); }}
            disabled={loading || !dimensions.length}
            className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            <Play className="h-4 w-4" />
            {loading ? 'Running...' : 'Run Report'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Results - Table */}
      {results && viewMode === 'table' && (
        results.rows.length > 0 ? (
          <div>
            {/* Totals summary */}
            <div className="mb-4 flex items-center gap-4">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Total: <span className="font-semibold text-gray-900 dark:text-gray-100">{results.totals?.count || 0}</span> talents
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Groups: <span className="font-semibold text-gray-900 dark:text-gray-100">{pagination.total_items}</span>
              </span>
            </div>

            <StatisticsTable
              data={results.rows}
              columns={tableColumns}
              isLoading={loading}
              rowKey={rowKey}
              visibleColumns={visibleColumns}
              footer={
                <Pagination
                  currentPage={pagination.current_page}
                  totalPages={pagination.total_pages}
                  totalItems={pagination.total_items}
                  itemsPerPage={pagination.items_per_page}
                  onPageChange={handlePageChange}
                  showResults="range"
                  perPageOptions={CUSTOM_REPORT_PAGINATION_OPTIONS}
                  perPage={limit}
                  onPerPageChange={(val) => { setLimit(parseInt(val)); setOffset(0); }}
                  showOnSinglePage
                  hasFirstLast
                  hasGoToPage
                />
              }
            />
          </div>
        ) : (
          <div className="flex justify-center items-center min-h-[200px] text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            No results for the selected dimensions and filters
          </div>
        )
      )}

      {/* Results - Chart */}
      {results && viewMode === 'chart' && (
        results.rows.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Bar chart */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {DIMENSION_OPTIONS.find(d => d.value === dimensions[0])?.label || dimensions[0]} — Bar Chart
              </h3>
              <HorizontalBarChart
                data={chartData}
                dataKey="count"
                name="Count"
                yAxisWidth={140}
                height={Math.max(300, chartData.length * 35)}
              />
            </div>

            {/* Pie chart */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {DIMENSION_OPTIONS.find(d => d.value === dimensions[0])?.label || dimensions[0]} — Distribution
              </h3>
              <DonutChart data={chartData} dataKey="count" height={300} />
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center min-h-[200px] text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            No results for the selected dimensions and filters
          </div>
        )
      )}

      {/* Initial state — no report run yet */}
      {!results && !loading && !error && (
        <div className="flex flex-col items-center justify-center min-h-[300px] text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <BarChart3 className="h-12 w-12 mb-3 opacity-50" />
          <p className="text-lg font-medium">Select dimensions and run the report</p>
          <p className="text-sm mt-1">Choose what to group by, optionally apply filters, then click "Run Report"</p>
        </div>
      )}

      {/* Loading overlay — only on initial run, before any results */}
      {loading && !results && (
        <div className="flex justify-center items-center min-h-[200px]">
          <Loader />
        </div>
      )}
    </div>
  );
};

export default CustomReportsStats;
